/**
 * Did the reviewers disagree about this paper?
 *
 * This is the one genuinely useful thing the old Decisions screen computed. That
 * screen has been removed — chairs decide from the submissions table and always
 * did — but "your reviewers contradicted each other on this one" is worth
 * surfacing, because it's exactly the paper a chair should read properly rather
 * than trusting the average.
 *
 * Deliberately NOT carried over: the automated accept/reject recommendation that
 * sat next to it. Its thresholds ("8 or above means accept") were somebody's
 * guess, never validated against real conferences, and a wrong suggestion
 * sitting beside a decision button is worse than no suggestion at all.
 *
 * The rule is a plain spread rather than a standard deviation: on a 1–10 scale,
 * two reviewers four or more points apart have genuinely different readings of
 * the paper. A spread is also something a chair can verify at a glance from the
 * scores in front of them, which a standard deviation is not.
 */

/** Minimum spread in overall scores that counts as disagreement, on a 1–10 scale. */
export const DISAGREEMENT_THRESHOLD = 4;

/**
 * @param {Array} reviews rows with an overall score, in any of the shapes the
 *   app uses (`overallScore`, `overall_score`, or `score`).
 * @returns {{ disagrees: boolean, min: number, max: number, range: number, count: number }}
 */
export function reviewAgreement(reviews) {
  const scores = (reviews || [])
    .map((r) => r?.overallScore ?? r?.overall_score ?? r?.score)
    .filter((s) => s !== null && s !== undefined && !Number.isNaN(Number(s)))
    .map(Number);

  // One opinion can't disagree with itself.
  if (scores.length < 2) {
    return { disagrees: false, min: null, max: null, range: 0, count: scores.length };
  }

  const min = Math.min(...scores);
  const max = Math.max(...scores);
  const range = max - min;

  return { disagrees: range >= DISAGREEMENT_THRESHOLD, min, max, range, count: scores.length };
}

export default reviewAgreement;
